'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, Download, Plus, TrendingUp, AlertCircle, DollarSign } from 'lucide-react';
import dynamic from 'next/dynamic';
import InvoiceTable from './InvoiceTable';
import InvoiceModal from './InvoiceModal';

const RevenueAreaChart = dynamic(() => import('./RevenueAreaChart'), { ssr: false });

export type Invoice = {
  id: string;
  invoiceNumber: string;
  accountHolder: string;
  feeType: string;
  amount: number;
  issuedDate: string;
  dueDate: string;
  status: string;
  cemetery: string;
  graveId: string;
  description: string;
  paidDate: string;
};

export const invoices: Invoice[] = [
  { id: 'inv-001', invoiceNumber: 'INV-2026-0401', accountHolder: 'Musa Nxumalo', feeType: 'Burial Fee', amount: 2800, issuedDate: '02 Aug 2026', dueDate: '16 Aug 2026', status: 'paid', cemetery: 'Mthatha', graveId: 'NYD-MTH-B2-R1-009', description: 'Burial fee for Thandi Nxumalo', paidDate: '03 Aug 2026' },
  { id: 'inv-002', invoiceNumber: 'INV-2026-0399', accountHolder: 'Zanele Ntanzi', feeType: 'Burial Fee', amount: 2800, issuedDate: '31 Jul 2026', dueDate: '14 Aug 2026', status: 'paid', cemetery: 'Libode', graveId: 'NYD-LIB-A2-R1-007', description: 'Burial fee for Bongani Ntanzi', paidDate: '04 Aug 2026' },
  { id: 'inv-003', invoiceNumber: 'INV-2026-0397', accountHolder: 'Thandi Maphumulo', feeType: 'Plot Purchase', amount: 5500, issuedDate: '29 Jul 2026', dueDate: '12 Aug 2026', status: 'paid', cemetery: 'Ngqeleni', graveId: 'NYD-NGQ-A1-R2-014', description: 'Plot purchase — permanent rights', paidDate: '03 Aug 2026' },
  { id: 'inv-004', invoiceNumber: 'INV-2026-0395', accountHolder: 'Siyanda Cele', feeType: 'Burial Fee', amount: 2800, issuedDate: '25 Jul 2026', dueDate: '08 Aug 2026', status: 'pending', cemetery: 'Ngqeleni', graveId: 'NYD-NGQ-B1-R3-011', description: 'Burial fee for Nosipho Cele', paidDate: '—' },
  { id: 'inv-005', invoiceNumber: 'INV-2026-0393', accountHolder: 'Lindiwe Mkhize', feeType: 'Maintenance Fee', amount: 450, issuedDate: '22 Jul 2026', dueDate: '05 Aug 2026', status: 'overdue', cemetery: 'Libode', graveId: 'NYD-LIB-B2-R4-027', description: 'Annual plot maintenance fee', paidDate: '—' },
  { id: 'inv-006', invoiceNumber: 'INV-2026-0391', accountHolder: 'Buhle Sithole', feeType: 'Burial Fee', amount: 2800, issuedDate: '26 Jul 2026', dueDate: '09 Aug 2026', status: 'pending', cemetery: 'Mthatha', graveId: 'NYD-MTH-D1-R1-003', description: 'Burial fee for Mandla Sithole', paidDate: '—' },
  { id: 'inv-007', invoiceNumber: 'INV-2026-0389', accountHolder: 'Nokuthula Khumalo', feeType: 'Plot Purchase', amount: 5500, issuedDate: '03 Aug 2026', dueDate: '17 Aug 2026', status: 'pending', cemetery: 'Libode', graveId: 'NYD-LIB-A3-R2-018', description: 'Plot purchase — permanent rights', paidDate: '—' },
  { id: 'inv-008', invoiceNumber: 'INV-2026-0387', accountHolder: 'Siphamandla Zulu', feeType: 'Burial Fee', amount: 2800, issuedDate: '02 Aug 2026', dueDate: '16 Aug 2026', status: 'paid', cemetery: 'Mthatha', graveId: 'NYD-MTH-B4-R3-022', description: 'Burial fee for Nokwanda Zulu', paidDate: '04 Aug 2026' },
  { id: 'inv-009', invoiceNumber: 'INV-2026-0385', accountHolder: 'Lungelo Dlamini', feeType: 'Burial Fee', amount: 2800, issuedDate: '03 Aug 2026', dueDate: '17 Aug 2026', status: 'pending', cemetery: 'Mthatha', graveId: 'NYD-MTH-C1-R4-031', description: 'Burial fee for Nomvula Dlamini', paidDate: '—' },
  { id: 'inv-010', invoiceNumber: 'INV-2026-0312', accountHolder: 'Phiwayinkosi Gumede', feeType: 'Maintenance Fee', amount: 450, issuedDate: '01 Jul 2026', dueDate: '15 Jul 2026', status: 'overdue', cemetery: 'Mthatha', graveId: '—', description: 'Annual plot maintenance fee', paidDate: '—' },
  { id: 'inv-011', invoiceNumber: 'INV-2026-0310', accountHolder: 'Zanele Dlamini', feeType: 'Plot Reservation', amount: 800, issuedDate: '28 Jul 2026', dueDate: '04 Aug 2026', status: 'overdue', cemetery: 'Mthatha', graveId: 'NYD-MTH-B3-R2-014', description: 'Plot reservation fee — 14-day hold', paidDate: '—' },
  { id: 'inv-012', invoiceNumber: 'INV-2026-0308', accountHolder: 'Thandi Maphumulo', feeType: 'Maintenance Fee', amount: 450, issuedDate: '15 Jul 2026', dueDate: '29 Jul 2026', status: 'partial', cemetery: 'Ngqeleni', graveId: 'NYD-NGQ-A1-R2-014', description: 'Annual plot maintenance fee', paidDate: '—' },
];

const STATUS_TABS = ['All', 'Pending', 'Paid', 'Overdue', 'Partial'];

export default function PaymentsBillingContent() {
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      const matchTab = activeTab === 'All' || inv.status === activeTab.toLowerCase();
      const matchSearch =
        !search ||
        inv.invoiceNumber.toLowerCase().includes(search.toLowerCase()) ||
        inv.accountHolder.toLowerCase().includes(search.toLowerCase()) ||
        inv.graveId.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [activeTab, search]);

  const tabCounts = useMemo(() => {
    const c: Record<string, number> = { All: invoices.length };
    STATUS_TABS.slice(1).forEach((t) => {
      c[t] = invoices.filter((i) => i.status === t.toLowerCase()).length;
    });
    return c;
  }, []);

  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const outstanding = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + i.amount, 0);
  const overdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Payments & Billing</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Invoices, receipts, and revenue management</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-sm border border-border"><Download size={14} /> Export</button>
          <button className="btn-primary text-sm"><Plus size={15} /> New Invoice</button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-base p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
              <TrendingUp size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Collected This Month</p>
              <p className="text-2xl font-700 tabular-nums text-primary">R {totalRevenue.toLocaleString('en-ZA')}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{invoices.filter(i => i.status === 'paid').length} invoices paid</p>
        </div>
        <div className="card-base p-5 border-warning/30 bg-[#fffbeb]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <DollarSign size={18} className="text-warning" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Outstanding Balance</p>
              <p className="text-2xl font-700 tabular-nums text-warning">R {outstanding.toLocaleString('en-ZA')}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{invoices.filter(i => i.status !== 'paid').length} unpaid invoices</p>
        </div>
        <div className="card-base p-5 border-negative/30 bg-[#fef2f2]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-negative/10 flex items-center justify-center">
              <AlertCircle size={18} className="text-negative" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Overdue Amount</p>
              <p className="text-2xl font-700 tabular-nums text-negative">R {overdue.toLocaleString('en-ZA')}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{invoices.filter(i => i.status === 'overdue').length} overdue invoices — action needed</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <RevenueAreaChart />

      {/* Status Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto scrollbar-thin">
        {STATUS_TABS.map((tab) => (
          <button
            key={`pay-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-500 whitespace-nowrap border-b-2 transition-all duration-150 ${
              activeTab === tab
                ? 'border-primary text-primary font-600' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
            {tabCounts[tab] > 0 && (
              <span className={`text-[11px] font-600 px-1.5 py-0.5 rounded-full ${
                activeTab === tab ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {tabCounts[tab]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by invoice, account holder, grave ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base pl-9 text-sm"
          />
        </div>
        <button className="btn-ghost text-sm border border-border"><Filter size={14} /> Filter</button>
        <span className="text-sm text-muted-foreground ml-auto">{filtered.length} invoices</span>
      </div>

      {/* Invoice Table */}
      <InvoiceTable invoices={filtered} onSelect={setSelectedInvoice} />

      {selectedInvoice && (
        <InvoiceModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
      )}
    </div>
  );
}